import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  ShieldCheck,
  Check,
  Users,
  Layers,
  Phone,
  Quote,
  Home,
  TrendingUp,
  Sparkles,
  UserPlus,
  ClipboardList,
  Rocket,
  Star,
  ChevronDown
} from 'lucide-react';

const About = () => {
  const navigate = useNavigate();
  const [activeFaq, setActiveFaq] = useState(0);

  const faqs = [
    {
      q: "What is the Top Interiors India vendor portal?",
      a: "It's an online platform where vendors can showcase their products and services and connect with architects, interior designers and industry professionals across India."
    },
    {
      q: "Who can register as a vendor?",
      a: "Any business in the architecture or interior design industry — furniture, lighting, hardware, marble and tile, modular kitchens, décor, materials or services."
    },
    {
      q: "What listing options are available?",
      a: "Basic (Free), Professional (₹12,000 / year) and Premium (₹25,000 / year). Each plan offers a different level of visibility and features."
    },
    {
      q: "How can designers contact vendors?",
      a: "Through the contact details on each vendor profile — phone, email, or the inquiry form on the platform."
    },
    {
      q: "Does Top Interiors India guarantee business leads?",
      a: "No. We provide visibility and networking. Actual inquiries depend on the vendor's offering and profile quality."
    },
    {
      q: "How long does a vendor listing remain active?",
      a: "Paid listings stay active for 12 months from the date of activation. You can renew or upgrade anytime."
    },
    {
      q: "Can vendors update their profile later?",
      a: "Yes. Vendors can update contact info, services and product information at any time through the dashboard."
    },
    {
      q: "Is the platform only for vendors?",
      a: "No. Interior designers, architects, contractors and homeowners can use the platform to discover vendors and explore products and services."
    }
  ];

  const features = [
    {
      IconComponent: ShieldCheck,
      title: "Verified vendor network",
      desc: "Every listing is reviewed by our team before going live, so professionals can connect with confidence."
    },
    {
      IconComponent: Layers,
      title: "Wide range of categories",
      desc: "Access vendors across multiple interior design, construction, and material supply categories on a single platform."
    },
    {
      IconComponent: Phone,
      title: "Direct vendor connection",
      desc: "Visitors can easily contact vendors to discuss project requirements, services, and product details."
    },
    {
      IconComponent: Home,
      title: "Industry-focused marketplace",
      desc: "A platform dedicated to the interiors and architecture industry, ensuring relevant connections."
    },
    {
      IconComponent: TrendingUp,
      title: "Growing professional network",
      desc: "Join a fast-growing community of active professionals in the design and construction industry."
    },
    {
      IconComponent: Sparkles,
      title: "User-friendly platform",
      desc: "Simple and easy-to-use interface to list products, manage inquiries, view analytics and connect with clients."
    }
  ];

  const steps = [
    {
      step: "Step 1",
      icon: <UserPlus className="w-6 h-6 text-primary" />,
      title: "Sign Up",
      desc: "Create your vendor profile by registering and entering basic business and contact information."
    },
    {
      step: "Step 2",
      icon: <ClipboardList className="w-6 h-6 text-primary" />,
      title: "Listing",
      desc: "Submit your business details, company information, address, portfolio images, and select category & tags for your profile."
    },
    {
      step: "Step 3",
      icon: <ShieldCheck className="w-6 h-6 text-primary" />,
      title: "Verification",
      desc: "Our team reviews and verifies your business details and credentials to maintain a high-standard vendor network."
    },
    {
      step: "Step 4",
      icon: <Rocket className="w-6 h-6 text-primary" />,
      title: "Profile Goes Live",
      desc: "Once approved, your listing goes live, allowing design professionals and clients to discover you and send business inquiries."
    }
  ];

  return (
    <div className="bg-white font-sans text-secondary overflow-hidden">
      {/* Hero Section */}
      <section className="pt-24 pb-16 md:pt-36 md:pb-24 bg-[#f2faf8] w-full border-b border-gray-100/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* Left Column */}
          <div className="lg:w-1/2 space-y-6 text-left">
            <span className="inline-block bg-[#eaf7f4] text-[#2ab391] border border-[#2ab391]/20 px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider">
              ABOUT US
            </span>
            <h1 className="text-3xl sm:text-5xl md:text-6xl font-extrabold text-secondary tracking-tight leading-tight">
              India's verified network of <span className="text-[#2ab391] italic font-heading font-normal">interior & construction</span> vendors.
            </h1>
            <p className="text-gray-500 text-sm sm:text-base md:text-lg font-medium leading-relaxed max-w-xl">
              Top Interiors India connects manufacturers, suppliers, contractors and brands with the architects, designers and homeowners who need them. Find, compare and connect with the best in the industry.
            </p>
            <div className="flex flex-wrap gap-4 pt-2">
              <button
                onClick={() => navigate('/listing')}
                className="bg-primary hover:bg-[#23977a] text-white py-3 px-7 rounded-full font-bold flex items-center gap-2 transition-all shadow-md active:scale-95 cursor-pointer animate-none"
              >
                Discover Vendors <ArrowRight size={16} />
              </button>
              <button
                onClick={() => navigate('/my-account/manage-listing')}
                className="bg-white border border-gray-300 hover:border-gray-400 text-gray-700 py-3 px-7 rounded-full font-bold transition-all hover:bg-gray-50 active:scale-95 cursor-pointer shadow-2xs"
              >
                List your business
              </button>
            </div>

            {/* Stats */}
            <div className="flex gap-8 sm:gap-12 pt-8 border-t border-gray-100">
              <div>
                <p className="text-2xl sm:text-3xl font-extrabold text-secondary">2K+</p>
                <p className="text-gray-400 text-xs sm:text-sm font-semibold mt-0.5">Verified Vendors</p>
              </div>
              <div>
                <p className="text-2xl sm:text-3xl font-extrabold text-secondary">21+</p>
                <p className="text-gray-400 text-xs sm:text-sm font-semibold mt-0.5">Categories</p>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="lg:w-1/2 relative w-full">
            <div className="rounded-3xl overflow-hidden shadow-2xl relative z-10 border border-gray-100/50">
              <img
                src="/About/about-vendor.jpg"
                alt="Verified Vendor Background"
                className="w-full h-auto object-cover max-h-[480px]"
              />
            </div>
            {/* Floating Checked Badge */}
            <div className="absolute bottom-4 left-4 sm:bottom-6 sm:left-6 bg-white py-2.5 px-4 sm:py-3.5 sm:px-5 rounded-xl sm:rounded-2xl shadow-xl flex items-center gap-2.5 sm:gap-3 border border-gray-100 z-20">
              <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-[#eaf7f4] flex items-center justify-center text-[#2ab391] flex-shrink-0">
                <Check size={14} className="stroke-[3] sm:hidden" />
                <Check size={18} className="stroke-[3] hidden sm:block" />
              </div>
              <div className="text-left">
                <p className="text-[10px] sm:text-xs font-extrabold text-secondary uppercase tracking-wider">Verified Vendors</p>
                <p className="text-[8px] sm:text-[10px] text-gray-400 font-bold">Hand-reviewed by our team</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Purpose Section */}
      <section className="py-16 md:py-24 bg-gray-50/50 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 items-start">
            <div className="lg:w-1/2 space-y-4 text-left">
              <span className="text-[#2ab391] text-xs sm:text-sm font-extrabold uppercase tracking-widest">
                OUR PURPOSE
              </span>
              <h2 className="text-2xl sm:text-4xl font-extrabold text-secondary leading-tight tracking-tight max-w-lg">
                Building a trusted bridge between vendors and the people who hire them.
              </h2>
            </div>
            <div className="lg:w-1/2 space-y-4 text-left text-gray-500 font-medium text-sm sm:text-base leading-relaxed">
              <p>
                We bring together category leaders, niche manufacturers, service providers, skilled contractors and interior design firms to help architects, designers, builders and homeowners find, compare and connect with the right partner for every project.
              </p>
              <p>
                Our verification process helps in establishing trust by checking vendor credentials, projects and portfolio quality, so that designers and clients can choose verified partners for their design projects.
              </p>
              <p>
                For vendors, we provide growth by providing them with visibility, qualified inquiries, and networking opportunities to grow their interiors business.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 md:py-24 bg-[#f2faf8] border-y border-gray-100/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-left max-w-3xl space-y-4 mb-16">
            <span className="text-[#2ab391] text-xs sm:text-sm font-extrabold uppercase tracking-widest">
              WHY CHOOSE US
            </span>
            <h2 className="text-2xl sm:text-4xl font-extrabold text-secondary tracking-tight">
              Built for the interiors industry — not generic listings.
            </h2>
            <p className="text-gray-500 text-sm sm:text-base font-medium leading-relaxed font-sans">
              We've designed every part of the platform around the specific needs of vendors, designers and architects working across India.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feat, idx) => {
              const Icon = feat.IconComponent;
              return (
                <div
                  key={idx}
                  className="p-8 bg-white hover:bg-[#eaf7f4] border border-gray-200/80 hover:border-transparent rounded-3xl transition-all duration-300 text-left flex flex-col justify-between shadow-2xs hover:shadow-md group cursor-pointer"
                >
                  <div className="space-y-5">
                    <div className="w-12 h-12 rounded-2xl bg-[#eaf7f4] group-hover:bg-[#2ab391] flex items-center justify-center flex-shrink-0 transition-colors duration-300">
                      <Icon className="w-5 h-5 text-primary group-hover:text-white transition-colors duration-300" />
                    </div>
                    <h4 className="text-lg font-bold text-secondary">{feat.title}</h4>
                    <p className="text-gray-500 text-sm font-medium leading-relaxed">{feat.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How Does It Work Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 mb-16">
            <div className="text-left space-y-3">
              <span className="text-[#2ab391] text-xs sm:text-sm font-extrabold uppercase tracking-widest">
                WORK FLOW
              </span>
              <h2 className="text-2xl sm:text-4xl font-extrabold text-secondary tracking-tight">
                How does it work?
              </h2>
            </div>
            <p className="text-gray-400 text-xs sm:text-sm font-semibold italic text-left max-w-md">
              Free listing includes registration, listing, verification, and live profile.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((item, idx) => (
              <div
                key={idx}
                className="relative bg-white pt-10 pb-8 px-8 rounded-3xl border border-gray-200/80 shadow-2xs text-left flex flex-col items-start gap-5 hover:shadow-xs transition-all duration-300"
              >
                <span className="absolute -top-3.5 left-8 text-[10px] font-extrabold text-white bg-secondary px-3.5 py-1 rounded-full uppercase tracking-wider shadow-2xs">
                  {item.step}
                </span>
                <div className="w-12 h-12 rounded-full bg-[#eaf7f4] flex items-center justify-center text-primary">
                  {item.icon}
                </div>
                <div className="space-y-2">
                  <h4 className="text-lg font-bold text-secondary">{item.title}</h4>
                  <p className="text-gray-500 text-sm font-medium leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 md:py-24 bg-secondary text-white relative">
        <div className="absolute top-0 left-0 w-64 h-64 bg-primary opacity-10 blur-3xl rounded-full pointer-events-none"></div>
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-emerald-700 opacity-10 blur-3xl rounded-full pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
          <div className="text-left space-y-3 mb-16">
            <span className="text-[#2ab391] text-xs sm:text-sm font-extrabold uppercase tracking-widest">
              FEEDBACK
            </span>
            <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight">
              What our vendors & clients say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full">
            {/* Testimonial 1 */}
            <div className="p-8 bg-[#132839] border border-[#1e3447] rounded-3xl text-left flex flex-col justify-between space-y-6 hover:bg-[#1a2f42] transition-colors relative shadow-sm">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <div className="flex gap-1 text-[#2ab391]">
                    {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
                  </div>
                  <Quote size={28} className="text-[#2ab391] opacity-30 transform rotate-180" />
                </div>
                <p className="text-gray-300 text-sm sm:text-base md:text-lg font-normal leading-relaxed font-sans">
                  "Listing on Top Interiors India brought us inquiries from designers in cities we didn't have physical stores. It's helped us expand our reach."
                </p>
              </div>
              <div className="flex items-center gap-3 pt-4 border-t border-white/5">
                <img
                  src="https://i.pravatar.cc/150?u=rajesh"
                  alt="Rajesh Sharma"
                  className="w-12 h-12 rounded-xl object-cover"
                />
                <div>
                  <h4 className="text-white font-bold text-sm">Rajesh Sharma</h4>
                  <p className="text-gray-450 text-xs font-semibold">Managing Director, Deluxe Kitchens</p>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="p-8 bg-[#132839] border border-[#1e3447] rounded-3xl text-left flex flex-col justify-between space-y-6 hover:bg-[#1a2f42] transition-colors relative shadow-sm">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <div className="flex gap-1 text-[#2ab391]">
                    {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
                  </div>
                  <Quote size={28} className="text-[#2ab391] opacity-30 transform rotate-180" />
                </div>
                <p className="text-gray-300 text-sm sm:text-base md:text-lg font-normal leading-relaxed font-sans">
                  "We find verified vendors for our projects within minutes. It's a huge time-saver and lets us source with absolute confidence."
                </p>
              </div>
              <div className="flex items-center gap-3 pt-4 border-t border-white/5">
                <img
                  src="https://i.pravatar.cc/150?u=meera"
                  alt="Meera Patel"
                  className="w-12 h-12 rounded-xl object-cover"
                />
                <div>
                  <h4 className="text-white font-bold text-sm">Meera Patel</h4>
                  <p className="text-gray-450 text-xs font-semibold">Principal Designer, Patel Design Co.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-start">
          {/* Left info column */}
          <div className="lg:w-1/3 text-left space-y-4">
            <span className="text-[#2ab391] text-xs sm:text-sm font-extrabold uppercase tracking-widest">
              FAQS
            </span>
            <h2 className="text-2xl sm:text-4xl font-extrabold text-secondary tracking-tight">
              Questions, answered.
            </h2>
            <p className="text-gray-500 text-sm sm:text-base font-medium leading-relaxed">
              Everything you need to know about listing, plans and using the platform. Can't find the answer? Contact our support.
            </p>
          </div>

          {/* Right accordion column */}
          <div className="lg:w-2/3 w-full space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-gray-200/80 rounded-xl overflow-hidden shadow-3xs bg-white transition-colors duration-200">
                <button
                  onClick={() => setActiveFaq(activeFaq === i ? -1 : i)}
                  className="w-full px-5 py-4 flex items-center justify-between transition-all bg-white text-secondary hover:bg-gray-50/50 border-none focus:outline-none cursor-pointer"
                >
                  <span className="font-bold text-sm sm:text-base text-left pr-4 leading-tight tracking-tight text-secondary">
                    {faq.q}
                  </span>
                  <ChevronDown
                    className={`transition-transform duration-300 flex-shrink-0 ${activeFaq === i ? 'rotate-180 text-primary' : 'text-gray-400'
                      }`}
                    size={18}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {activeFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: "easeInOut" }}
                      className="overflow-hidden bg-white"
                    >
                      <div className="px-5 py-4 border-t border-gray-100 text-left text-xs sm:text-sm text-gray-500 font-medium leading-relaxed font-sans">
                        {faq.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="pb-16 md:pb-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="bg-gradient-to-r from-[#00b07b] to-[#005a43] rounded-3xl p-8 sm:p-12 md:p-16 text-white relative overflow-hidden shadow-xl border border-white/5">
          <div className="absolute top-0 right-0 w-80 h-80 bg-emerald-600 opacity-20 blur-3xl rounded-full pointer-events-none"></div>
          <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-teal-500 opacity-20 blur-3xl rounded-full pointer-events-none"></div>

          <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-8 text-center md:text-left">
            <div className="space-y-4 max-w-2xl">
              <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight">
                Ready to grow your interiors business?
              </h2>
              <p className="text-teal-50 text-sm sm:text-base font-medium leading-relaxed">
                Join 2,000+ verified vendors getting discovered by designers, architects and homeowners every day.
              </p>
            </div>

            <div className="flex flex-wrap justify-center md:justify-end gap-4 flex-shrink-0">
              <button
                onClick={() => navigate('/my-account/manage-listing')}
                className="bg-white hover:bg-gray-100 text-[#005a43] py-3.5 px-7 rounded-xl font-bold transition-all active:scale-95 cursor-pointer shadow-md"
              >
                List your business
              </button>
              <button
                onClick={() => navigate('/listing')}
                className="bg-transparent hover:bg-white/10 text-white border border-white/30 hover:border-white/50 py-3.5 px-7 rounded-xl font-bold transition-all active:scale-95 cursor-pointer"
              >
                Browse vendors
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
